import * as yup from "yup";

/**
 * Schéma de validation pour le formulaire d'inscription en Back Office
 * Adapté du schéma FO avec les champs spécifiques au BO
 * Le schéma vérifie dynamiquement le pays pour appliquer les règles de validation appropriées
 */
export const getInscriptionSchemaBO = (isFranceOrAssimile: boolean = false, sirenApiError?: string | null, emailApiError?: string | null) => {
  return yup.object().shape({
    Email: yup.string()
      .email("Email invalide")
      .required("Email requis")
      .test("email-api-validation", emailApiError || "", function(value) {
        // Si une erreur API existe (email déjà utilisé), le champ est invalide
        if (emailApiError && value) {
          return this.createError({ message: emailApiError });
        }
        return true;
      }),
    IdPays: yup.number().required("Pays requis").transform((value) => (isNaN(value) ? undefined : value)),
    // Validation du SIREN avec gestion de l'erreur API
    Siren: isFranceOrAssimile
      ? yup.string()
          .required("SIREN requis pour la France")
          .length(9, "Le SIREN doit contenir 9 chiffres")
          .test("api-validation", sirenApiError || "", function(value) {
            // Si une erreur API existe, le champ est invalide
            if (sirenApiError && value && value.length === 9) {
              return this.createError({ message: sirenApiError });
            }
            return true;
          })
      : yup.string()
          .optional()
          .nullable()
          .test("siren-length", "Le SIREN doit contenir 9 chiffres", function(value) {
            // Vérifier le format si une valeur est saisie
            if (value && value.length > 0 && value.length !== 9) {
              return false;
            }
            // Vérifier l'erreur API même pour les autres pays
            if (sirenApiError && value && value.length === 9) {
              return this.createError({ message: sirenApiError });
            }
            return true;
          }),
    RaisonSociale: yup.string().required("Raison sociale requise"),
    Adresse: yup.string().required("Adresse requise"),
    CodePostal: yup.string().optional().nullable(),
    Ville: yup.string().required("Ville requise"),
    SiteInternet: yup.string().optional().nullable().matches(
      /^$|^(https?:\/\/)?([\w\u00C0-\u00FF-]+(\.[\w\u00C0-\u00FF-]+)+)(:\d+)?(\/[\w\u00C0-\u00FF\-._~:/?#[\]@!$&'()*+,;=%]*)?$/,
      "Veuillez entrer une URL valide (ex: https://editeurspros.electre.com/)"
    ),
    IdCivilite: yup.number().required("Civilité requise").transform((value) => (isNaN(value) ? undefined : value)),
    Nom: yup.string().required("Nom requis"),
    Prenom: yup.string().required("Prénom requis"),
    // Validation internationale pour les numéros de téléphone (format E.164)
    Telephone: yup.string().optional().nullable().matches(
      /^$|^\+[1-9]\d{1,14}$/,
      "Format de téléphone invalide. Veuillez sélectionner un pays et saisir un numéro valide."
    ),
    IdFonction: yup.number().required("Fonction requise").transform((value) => (isNaN(value) ? undefined : value)),
    GLNEditeur: yup.string().optional().nullable().matches(/^\d{13}$|^$/, "Le GLN doit contenir 13 chiffres"),
    GLNDiffuseur: yup.string().optional().nullable().matches(/^\d{13}$|^$/, "Le GLN doit contenir 13 chiffres"),
    Marque: yup.string().optional().nullable(),
    IdSecteurActivite: yup.number().required("Secteur d'activité requis").transform((value) => (isNaN(value) ? undefined : value)),
    ISBN: yup.string().optional().nullable(),
    ISBN13EANComplet: yup.string().optional().nullable(),
    Commentaires: yup.string().optional().nullable().max(600, "Le message ne doit pas dépasser 600 caractères"),
    IdTypeDeCompteSociete: yup.number()
      .transform((value, originalValue) => {
        // Si la valeur originale est vide ou non numérique, retourner undefined
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
          return undefined;
        }
        const parsed = Number(originalValue);
        return isNaN(parsed) ? undefined : parsed;
      })
      .required("Type de compte requis"),
    AcceptationCGU: yup.boolean().oneOf([true], "Vous devez accepter la convention").required("Vous devez accepter la convention"),
  });
};
